import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../ui/button'
import { Checkbox } from '../ui/checkbox'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table'
import { DeleteConfirmDialog } from '../ui/delete-confirm-dialog'
import {
  formatCents,
  microsToEuros,
  useAdminBillingConsumptions,
  useRecloseAdminBillingMonth,
} from '../../hooks/use-admin-billing'

/**
 * BillingConsumptionSection lists the closed months of one organization:
 * quantity in billing units, unit price, amount, total. 0 EUR lines are hidden
 * behind a toggle (spec 2026-10-05-13); storage keeps them.
 */
export function BillingConsumptionSection({ orgUid }: { orgUid: string }) {
  const { t, i18n } = useTranslation('admin')
  const lang = i18n.language.startsWith('fr') ? 'fr' : 'en'
  const { data, isLoading } = useAdminBillingConsumptions(orgUid)
  const reclose = useRecloseAdminBillingMonth()
  const [showAll, setShowAll] = useState(false)
  const [error, setError] = useState(false)
  const nf = new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 3 })

  const codes = data?.codes ?? []
  const label = (c: string) => {
    const found = codes.find((x) => x.code === c)
    return found ? (found.label[lang] ?? found.label.en) : c
  }

  return (
    <section className="space-y-4" data-testid="billing-consumptions">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <div>
          <h2 className="text-lg font-semibold">{t('billing.consumption.title')}</h2>
          <p className="text-sm text-muted-foreground">{t('billing.consumption.help')}</p>
        </div>
        <label className="flex shrink-0 items-center gap-2 whitespace-nowrap text-sm">
          <Checkbox checked={showAll} onCheckedChange={setShowAll} data-testid="billing-show-all-lines" />
          {t('billing.consumption.show_all')}
        </label>
      </div>
      {error && <div className="text-sm text-destructive">{t('billing.consumption.reclose_error')}</div>}
      {isLoading ? (
        <div className="text-muted-foreground">{t('billing.loading')}</div>
      ) : (data?.data ?? []).length === 0 ? (
        <div className="bg-card rounded-lg border border-border px-6 py-4 text-center text-muted-foreground" data-testid="billing-consumptions-empty">
          {t('billing.consumption.empty')}
        </div>
      ) : (
        (data?.data ?? []).map((m) => {
          const lines = m.lines.filter((l) => showAll || l.amount_cents !== 0)
          return (
            <div key={m.month} className="bg-card rounded-lg border border-border overflow-hidden" data-testid={`billing-month-${m.month.slice(0, 7)}`}>
              <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                <div className="font-medium">{m.month.slice(0, 7)}</div>
                <div className="flex items-center gap-4">
                  <span className="tabular-nums font-medium" data-testid={`billing-month-total-${m.month.slice(0, 7)}`}>
                    {formatCents(m.total_cents, i18n.language)}
                  </span>
                  <DeleteConfirmDialog
                    title={t('billing.consumption.reclose_title')}
                    description={t('billing.consumption.reclose_description', { month: m.month.slice(0, 7) })}
                    isDeleting={reclose.isPending}
                    onConfirm={() => {
                      setError(false)
                      reclose.mutate(
                        { params: { path: { orgUid, month: m.month } } },
                        { onError: () => setError(true) },
                      )
                    }}
                    trigger={
                      <Button variant="outline" size="sm" data-testid={`billing-reclose-${m.month.slice(0, 7)}`}>
                        {t('billing.consumption.reclose')}
                      </Button>
                    }
                  />
                </div>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('billing.prices.column.code')}</TableHead>
                    <TableHead className="text-right">{t('billing.consumption.column.quantity')}</TableHead>
                    <TableHead className="text-right">{t('billing.consumption.column.unit_price')}</TableHead>
                    <TableHead className="text-right">{t('billing.consumption.column.amount')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lines.map((l) => (
                    <TableRow key={l.code} data-testid={`billing-line-${m.month.slice(0, 7)}-${l.code}`}>
                      <TableCell>{label(l.code)}</TableCell>
                      <TableCell className="text-right tabular-nums">{nf.format(l.quantity / l.divisor)}</TableCell>
                      <TableCell className="text-right tabular-nums">{microsToEuros(l.unit_price_micros, i18n.language)}
                        {l.price_period === 'year' && <span className="text-xs text-muted-foreground"> {t('billing.prices.period.year')}</span>}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatCents(l.amount_cents, i18n.language)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )
        })
      )}
    </section>
  )
}
